import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useRegistration, emptyParticipant } from "../context/RegistrationContext";
import Navbar from "../components/Navbar";
import OrderSummary from "../components/OrderSummary";
import { STEPS, ProgressBar, ParticipantsStep, ServicesStep, GuardianStep, ReviewStep, WizardNav } from "../components/register/RegisterSteps";
import { participantsError, guardianError } from "../lib/registrationValidation";

const ClosedNotice = () => (
  <div><Navbar />
    <div className="max-w-xl mx-auto px-4 py-24 text-center">
      <h1 className="font-display text-3xl font-bold text-[#03281B]">Registration Closed</h1>
      <p className="text-slate-600 mt-3">Registration for Sporting CP Camp San Diego 2027 is now closed.</p>
    </div>
  </div>
);

const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

export default function Register() {
  const navigate = useNavigate();
  const reg = useRegistration();
  const { config, participants, setParticipants, guardian, consents, giftRecipientIndex, setGiftRecipientIndex, quote } = reg;
  const [step, setStep] = useState(0);

  if (config?.registration_closed) return <ClosedNotice />;

  const updateParticipant = (i, next) => setParticipants(participants.map((p, idx) => (idx === i ? next : p)));
  const setP = (i, patch) => updateParticipant(i, { ...participants[i], ...patch });
  const addParticipant = () => { setParticipants([...participants, emptyParticipant()]); toast.success("Participant added"); };
  const removeParticipant = (i) => {
    if (participants.length === 1) return;
    setParticipants(participants.filter((_, idx) => idx !== i));
    if (giftRecipientIndex >= participants.length - 1) setGiftRecipientIndex(0);
  };

  const stepError = () => {
    if (step === 0) return participantsError(participants);
    if (step === 2) return guardianError(guardian, consents);
    return null;
  };
  const next = () => {
    const err = stepError();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    scrollTop();
  };
  const back = () => { setStep((s) => Math.max(s - 1, 0)); scrollTop(); };
  const proceedToCheckout = () => {
    if (!quote || !quote.total) { toast.error("Unable to calculate total. Please review your selections."); return; }
    navigate("/checkout");
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ProgressBar step={step} />
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {step === 0 && <ParticipantsStep participants={participants} sizes={config?.clothing_sizes || { tshirt: [], shorts: [], socks: [] }} setP={setP} onAdd={addParticipant} onRemove={removeParticipant} />}
            {step === 1 && <ServicesStep participants={participants} updateParticipant={updateParticipant} />}
            {step === 2 && (
              <GuardianStep guardian={guardian} setGuardian={reg.setGuardian} consents={consents} setConsents={reg.setConsents}
                participants={participants} showGift={config?.gifts?.remaining > 0 && participants.length > 1}
                giftRecipientIndex={giftRecipientIndex} setGiftRecipientIndex={setGiftRecipientIndex} />
            )}
            {step === 3 && <ReviewStep quote={quote} guardian={guardian} />}
            <WizardNav step={step} onBack={back} onNext={next} onCheckout={proceedToCheckout} />
          </div>
          <div className="lg:col-span-1"><div className="lg:sticky lg:top-20"><OrderSummary /></div></div>
        </div>
      </div>
    </div>
  );
}
